import { useState } from "react";
import {
  collectFieldErrors,
  EMAIL_INVALID_MSG,
  emailRule,
  type FieldErrors,
} from "@shared/validators";
import { ContactInfoFields } from "@/components/ui/ContactInfoFields";
import { DateInputNZ } from "@/components/ui/DateInputNZ";
import { FieldError } from "@/components/ui/FieldError";
import { InquiryShell } from "./InquiryShell";
import { InquirySuccess } from "./InquirySuccess";
import { track } from "@/lib/analytics";
import { StepFooter } from "./StepFooter";
import { buildQuickInquiryFormData, type QuickInquiryDraft } from "./submit-inquiry";
import { useInquirySubmit } from "./useInquirySubmit";
import type { InquiryProfileConfig } from "./inquiry-profile-schema";
import step from "./step.module.css";
import styles from "./QuickInquiryForm.module.css";

interface QuickInquiryFormProps {
  profile: InquiryProfileConfig;
  draft: QuickInquiryDraft;
  onDraftChange: (draft: QuickInquiryDraft) => void;
  onBack: () => void;
}

type QuickInquiryField = "contactName" | "requestSummary" | "contactEmail";

function validate(draft: QuickInquiryDraft): FieldErrors<QuickInquiryField> {
  return collectFieldErrors<QuickInquiryField>({
    contactName: {
      value: draft.contactName,
      rule: { required: true, emptyMsg: "Enter your name" },
    },
    requestSummary: {
      value: draft.requestSummary,
      rule: { required: true, emptyMsg: "Tell us briefly what you need" },
    },
    contactEmail: {
      value: draft.contactEmail,
      rule: { required: true, emptyMsg: EMAIL_INVALID_MSG, ...emailRule },
    },
  });
}

export function QuickInquiryForm({
  profile,
  draft,
  onDraftChange,
  onBack,
}: QuickInquiryFormProps): React.JSX.Element {
  const [errors, setErrors] = useState<FieldErrors<QuickInquiryField>>({});
  const lifecycle = useInquirySubmit({
    profileKey: profile.key,
    path: "quick_enquiry",
    build: (submissionKey) => buildQuickInquiryFormData({ profile, draft, submissionKey }),
  });

  const update = <K extends keyof QuickInquiryDraft>(
    field: K,
    value: QuickInquiryDraft[K],
  ): void => {
    onDraftChange({ ...draft, [field]: value });
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const submit = (): void => {
    const nextErrors = validate(draft);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      track("field_error_shown", {
        profileKey: profile.key,
        path: "quick_enquiry",
        fieldNames: Object.keys(nextErrors),
      });
      return;
    }
    void lifecycle.submit();
  };

  if (lifecycle.state.status === "success") {
    return (
      <InquirySuccess
        profile={profile}
        path="quick_enquiry"
        statusToken={lifecycle.state.statusToken}
        title="Enquiry received"
        staticSubtitle="We'll get back to you by email soon."
      />
    );
  }

  return (
    <InquiryShell
      company={
        profile.company
          ? { name: profile.company.name, url: profile.company.publicUrl }
          : undefined
      }
    >
      <div className={step.step}>
        <h1 className={step.heading}>Ask a question</h1>
        <p className={styles.intro}>
          Flick us the basics and we&apos;ll get back to you by email.
        </p>

        <div className={styles.fields}>
          <ContactInfoFields
            contactName={draft.contactName}
            contactEmail={draft.contactEmail}
            showValidation
            labels={{ name: "Your name", email: "Email address" }}
            nameError={errors.contactName}
            emailError={errors.contactEmail}
            onNameChange={(value) => update("contactName", value)}
            onEmailChange={(value) => update("contactEmail", value)}
            showPhoneField={false}
          />

          <label className={styles.field}>
            <span className={styles.label}>
              What do you need?
            </span>
            <textarea
              value={draft.requestSummary}
              onChange={(event) => update("requestSummary", event.target.value)}
              rows={5}
              maxLength={5000}
              className={styles.question}
            />
            <FieldError message={errors.requestSummary} />
          </label>

          <label className={styles.field}>
            <span className={styles.label}>
              Event name{" "}
              <span className={styles.optional}>(optional)</span>
            </span>
            <input
              value={draft.eventName}
              onChange={(event) => update("eventName", event.target.value)}
              maxLength={200}
              className={styles.input}
            />
          </label>

          <div className={styles.optionalPair}>
            <label className={styles.field}>
              <span className={styles.label}>
                Event date{" "}
                <span className={styles.optional}>(optional)</span>
              </span>
              <DateInputNZ
                value={draft.eventDate}
                onChange={(iso) => update("eventDate", iso)}
                  />
            </label>
            <label className={styles.field}>
              <span className={styles.label}>
                Location{" "}
                <span className={styles.optional}>(optional)</span>
              </span>
              <input
                value={draft.location}
                onChange={(event) => update("location", event.target.value)}
                className={styles.input}
              />
            </label>
          </div>

          {lifecycle.submitError && (
            <p role="alert" className={styles.submitError}>
              {lifecycle.submitError}
            </p>
          )}
        </div>
        <StepFooter
          onBack={onBack}
          backLabel="Back to options"
          onContinue={submit}
          continueLabel="Send my question"
          loading={lifecycle.isSubmitting}
        />
      </div>
    </InquiryShell>
  );
}
